import React from 'react';
import PropTypes from 'prop-types';
import { withRouter, routerShape } from 'react-router';
import noop from 'lodash/noop';
import { defineMessages, injectIntl } from 'react-intl';
import GenericModal from '../GenericModal';

const messages = defineMessages({
  title: {
    id: 'amnext.unsaved.title',
    defaultMessage: 'You have unsaved changes',
  },
  text: {
    id: 'amnext.unsaved.text',
    defaultMessage: 'You have unsaved changes, would you like to save them?',
  },
  confirm: {
    id: 'amnext.unsaved.confirm',
    defaultMessage: 'SAVE',
  },
  discard: {
    id: 'amnext.unsaved.discard',
    defaultMessage: 'Don\'t save changes',
  },
});

/**
 * Displays a modal dialog when user initiates a route change asking whether
 * to save or discard changes. Once the user opts for either choice, and the
 * corresponding action callbacks have completed successfully, the route change
 * will be performed.
 * If `closeable` is set to `true`, the dialog displays a close icon that closes
 * the dialog without the route change taking effect.
 * No dialog displays if `when` is set to `false`.
 *
 * Usage:
 *
 * ```
 *   render() {
 *     return (
 *       <div>
 *        <UnsavedChangesPrompt
 *          when={this.isDirty()}
 *          closeable
 *          saveChanges={this.save}
 *          discardChanges={this.discard}
 *         />
 *       </div>
 *     )
 *   }
 * ```
 */
class UnsavedChangesPrompt extends React.Component {
  static propTypes = {
    // injectIntl
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    // withRouter
    router: routerShape.isRequired,
    routes: PropTypes.arrayOf(PropTypes.object).isRequired,
    // own
    saveChanges: PropTypes.func.isRequired,
    discardChanges: PropTypes.func,
    // cb invoked whenever the dialog is closed as a direct or indirect result of user action (discard, save, close)
    //  the callback is passed a parameter `abort=true` if the route transition was not invoked for any reason.
    onClose: PropTypes.func,
    when: PropTypes.bool,
    closeable: PropTypes.bool,
  };

  static defaultProps = {
    when: false,
    closeable: false,
    discardChanges: () => Promise.resolve(),
    onClose: noop,
  };

  state = {
    targetLocation: null,
  };

  removeRouteLeaveHook = noop;

  componentDidMount() {
    this.configureLeaveHook();
  }

  componentDidUpdate(prevProps) {
    /* istanbul ignore else */
    if (prevProps.when !== this.props.when) {
      this.configureLeaveHook();
    }
  }

  componentWillUnmount() {
    this.removeRouteLeaveHook();
  }

  onConfirm = async () => {
    const { router, saveChanges, onClose } = this.props;
    const { targetLocation } = this.state;
    this.removeRouteLeaveHook();
    this.clearTargetLocation();
    try {
      await saveChanges();
      onClose();
      router.push(targetLocation.pathname);
    } catch (e) {
      this.configureLeaveHook();
      onClose(true);
    }
  };

  onDiscard = async () => {
    const { router, discardChanges, onClose } = this.props;
    const { targetLocation } = this.state;
    this.removeRouteLeaveHook();
    this.clearTargetLocation();
    // there are no expected errors, and we are ok crashing because
    //  hook and state have been cleared up already.
    await discardChanges();
    onClose();
    router.push(targetLocation.pathname);
  };

  onClose = () => {
    const { onClose } = this.props;
    this.clearTargetLocation();
    onClose(true);
  };

  setTargetLocation(targetLocation) {
    this.setState({ targetLocation });
  }

  clearTargetLocation() {
    this.setState({ targetLocation: null });
  }

  handleRouteLeave = (targetLocation) => {
    this.setTargetLocation(targetLocation);
    return false;
  };

  handleBeforeUnload = (e) => {
    // please note that all modern browsers no longer display the suggested message
    const msg = 'You have unsaved changes, are you sure you want to leave?';
    e.preventDefault();
    // chrome needs "returnValue" prop to be set
    e.returnValue = msg;
    return msg;
  };

  configureLeaveHook() {
    const { when } = this.props;
    this.removeRouteLeaveHook();
    if (when) {
      // simply re-install the hook - it's cheap
      this.addRouteLeaveHook();
    }
  }

  addRouteLeaveHook() {
    const { router, routes } = this.props;
    const route = routes[routes.length - 1];
    const removeHook = router.setRouteLeaveHook(route, this.handleRouteLeave);
    this.removeRouteLeaveHook = () => {
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
      removeHook();
      this.removeRouteLeaveHook = noop;
    };
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  render() {
    const { when, intl, closeable } = this.props;
    const { targetLocation } = this.state;
    if (!when || !targetLocation) {
      return null;
    }
    return (
      <GenericModal
        open
        onConfirm={this.onConfirm}
        onCancel={this.onDiscard}
        onClose={this.onClose}
        title={intl.formatMessage(messages.title)}
        message={intl.formatMessage(messages.text)}
        confirmText={intl.formatMessage(messages.confirm)}
        cancelText={intl.formatMessage(messages.discard)}
        closeable={closeable}
      />
    );
  }
}

export default withRouter(injectIntl(UnsavedChangesPrompt));
